/**
 * Canonical journey model · unifies PERT modules, constructive phases, and resources.
 * Source: modulos.json + arquivos-uteis.json (never edits modulos/ transcripts).
 */

import modulosData from '@content/modulos.json'
import { getRecursoIdsForTopico } from '@content/recursos.js'
import { FASES_CONSTRUTIVAS, MODULOS, TOTAL_TOPICOS } from '@utils/constants.js'
import { getTopicoContent } from '@content/topicos-content.js'
import { getTopicoTipo } from '@utils/topicoTipo.js'

export type CamadaJornada = 'decisao' | 'fisica' | 'legal'

export interface PassoJornada {
  globalIndex: number
  topicoId: string
  slug: string
  titulo: string
  ordemModulo: number
  moduloId: string
  moduloTitulo: string
  moduloIcone: string
  moduloOrdem: number
  faseConstrutivaId: string | null
  camada: CamadaJornada
  temFerramenta: boolean
  temChecklist: boolean
  recursosIds: string[]
  critico: boolean
}

export interface ModuloJornada {
  id: string
  titulo: string
  icone: string
  ordem: number
  camada: CamadaJornada
  faseConstrutivaId: string | null
  passos: PassoJornada[]
}

/** Maps decision modules to related constructive phase (when applicable). */
const MODULO_FASE_MAP: Record<string, string | null> = {
  terreno: 'pre-obra',
  'sistemas-construtivos': 'pre-obra',
  projetos: 'pre-obra',
  'planejamento-financeiro': 'pre-obra',
  'mao-de-obra': 'pre-obra',
  execucao: 'infraestrutura',
  tributos: 'entrega',
}

const MODULO_CAMADA: Record<string, CamadaJornada> = {
  terreno: 'decisao',
  'sistemas-construtivos': 'decisao',
  projetos: 'decisao',
  'planejamento-financeiro': 'decisao',
  'mao-de-obra': 'decisao',
  execucao: 'fisica',
  tributos: 'legal',
}

function buildPassos(): PassoJornada[] {
  const passos: PassoJornada[] = []
  let globalIndex = 0

  for (const m of modulosData.modulos) {
    const moduloMeta = MODULOS.find((x) => x.id === m.id)
    for (const t of m.topicos) {
      globalIndex += 1
      const content = getTopicoContent(t.id)
      const tipo = getTopicoTipo(m.id, t.slug, t.id)
      const temFerramenta = tipo === 'ferramenta' || (content?.temFerramenta ?? false)
      const checklistLen = content?.checklistAplicabilidade.length ?? 0

      passos.push({
        globalIndex,
        topicoId: t.id,
        slug: t.slug,
        titulo: t.titulo,
        ordemModulo: t.ordem,
        moduloId: m.id,
        moduloTitulo: m.titulo,
        moduloIcone: moduloMeta?.icone ?? m.icone,
        moduloOrdem: m.ordem,
        faseConstrutivaId: MODULO_FASE_MAP[m.id] ?? null,
        camada: MODULO_CAMADA[m.id] ?? 'decisao',
        temFerramenta,
        temChecklist: checklistLen > 0,
        recursosIds: getRecursoIdsForTopico(t.id),
        critico: temFerramenta || checklistLen >= 4,
      })
    }
  }

  return passos
}

const ALL_PASSOS = buildPassos()

export function getAllPassos(): PassoJornada[] {
  return ALL_PASSOS
}

export function getModulosJornada(): ModuloJornada[] {
  return modulosData.modulos.map((m) => ({
    id: m.id,
    titulo: m.titulo,
    icone: MODULOS.find((x) => x.id === m.id)?.icone ?? m.icone,
    ordem: m.ordem,
    camada: MODULO_CAMADA[m.id] ?? 'decisao',
    faseConstrutivaId: MODULO_FASE_MAP[m.id] ?? null,
    passos: ALL_PASSOS.filter((p) => p.moduloId === m.id),
  }))
}

export function getPassoByTopicoId(topicoId: string): PassoJornada | undefined {
  return ALL_PASSOS.find((p) => p.topicoId === topicoId)
}

export function getPassoAnterior(topicoId: string): PassoJornada | null {
  const idx = ALL_PASSOS.findIndex((p) => p.topicoId === topicoId)
  if (idx <= 0) return null
  return ALL_PASSOS[idx - 1] ?? null
}

export function getPassoProximo(topicoId: string): PassoJornada | null {
  const idx = ALL_PASSOS.findIndex((p) => p.topicoId === topicoId)
  if (idx < 0 || idx >= ALL_PASSOS.length - 1) return null
  return ALL_PASSOS[idx + 1] ?? null
}

export { TOTAL_TOPICOS, FASES_CONSTRUTIVAS }
